import { Role } from '@/@auth/user';
import { getNestedBoolean, getNestedString } from '@/utils/useConfig';
import { ConfigValues } from '@/utils/configManager';
import { FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import defaultNavigationConfig from './navigationConfig';

/**
 * Configuration mapping for navigation items
 * Maps navigation item IDs to their corresponding configuration names and customizable property paths
 *
 * Example config structure for customization:
 * {
 *   "disablePage": false,
 *   "permissions": ["Admin", "Billing"],
 *   "navigation": {
 *     "title": "Custom Title",
 *     "subtitle": "Custom Subtitle",
 *     "icon": "heroicons-outline:star",
 *     "order": 1,
 *     "children": {
 *       "webrar-dashboard": {
 *         "title": "Custom Dashboard Title",
 *         "icon": "heroicons-outline:home",
 *         "order": 2,
 *         "permissions": ["Admin", "Technical Support"]
 *       },
 *       "webrar-currentDomains": {
 *         "title": "Custom Domains Title",
 *         "icon": "heroicons-outline:globe-alt",
 *         "order": 1,
 *         "permissions": ["Admin", "Domain Manager"]
 *       }
 *     }
 *   }
 * }
 * Note: Lower order values appear first. Items without order retain their original position.
 * Note: If permissions array is empty or not specified, the item is visible to all users.
 */

type NavigationConfigMapping = {
    // Name of the config
    configName: string;
    // Whether a page should be disabled ot not, if it is disabled
    disablePath?: string;
    // Caters to hide the path but can still be accessible via the URL
    hiddenPath?: string;
    titlePath: string;
    subtitlePath: string;
    childrenConfigPath: string;
    permissionsPath: string;
};

export const navigationConfigMap: Record<string, NavigationConfigMapping> = {
    account: {
        configName: 'account_config',
        disablePath: 'disablePage',
        titlePath: 'navigation.title',
        subtitlePath: 'navigation.subtitle',
        childrenConfigPath: 'navigation.children',
        permissionsPath: 'permissions'
    },
    mis: {
        configName: 'mis_config',
        disablePath: 'disablePage',
        titlePath: 'navigation.title',
        subtitlePath: 'navigation.subtitle',
        childrenConfigPath: 'navigation.children',
        permissionsPath: 'permissions'
    },
    billing: {
        configName: 'billing_config',
        disablePath: 'disablePage',
        titlePath: 'navigation.title',
        subtitlePath: 'navigation.subtitle',
        childrenConfigPath: 'navigation.children',
        permissionsPath: 'permissions'
    },
    webrar: {
        configName: 'webrar_config',
        disablePath: 'disablePage',
        titlePath: 'navigation.title',
        subtitlePath: 'navigation.subtitle',
        childrenConfigPath: 'navigation.children',
        permissionsPath: 'permissions'
    },
    zoneeditor: {
        configName: 'zone_editor_config',
        disablePath: 'disablePage',
        titlePath: 'navigation.title',
        subtitlePath: 'navigation.subtitle',
        childrenConfigPath: 'navigation.children',
        permissionsPath: 'permissions'
    },
    onboarding: {
        configName: 'onboarding_config',
        disablePath: 'disablePage',
        titlePath: 'navigation.title',
        subtitlePath: 'navigation.subtitle',
        childrenConfigPath: 'navigation.children',
        permissionsPath: 'permissions'
    },
    support: {
        configName: 'navbar_config',
        disablePath: 'support.disablePage',
        titlePath: 'support.navigation.title',
        subtitlePath: 'support.navigation.subtitle',
        childrenConfigPath: 'support.navigation.children',
        permissionsPath: 'support.permissions'
    },
    
} as const;

/**
 * Check if user has permission to access a navigation item
 * @param requiredPermissions - Array of role strings required to access the item
 * @param userRoles - Array of user's roles
 * @returns true if user has at least one required permission, or if no permissions are required
 */
const hasPermission = (
    requiredPermissions: ConfigValues | undefined,
    userRoles: Role[] | null | undefined
): boolean => {
    // If no permissions required, allow access
    if (!requiredPermissions || !Array.isArray(requiredPermissions)) {
        return true;
    }

    // If user has no roles, deny access to permission-restricted items
    if (!userRoles || userRoles.length === 0) {
        return false;
    }

    // Check if user has at least one of the required permissions
    return requiredPermissions.some(permission =>
        typeof permission === 'string' && userRoles.some(role => role === permission)
    );
};

/**
 * Apply configuration overrides to a single child navigation item
 * @param child - The child navigation item
 * @param childConfig - Configuration for this specific child
 * @returns Child navigation item with applied customizations
 */
const applyConfigToChild = (
    child: FuseNavItemType,
    childConfig: ConfigValues
): FuseNavItemType => {
    const customizations: Partial<FuseNavItemType> = {};

    // Apply custom title if configured
    const customTitle = getNestedString(childConfig, 'title', '');

    if (customTitle) {
        customizations.title = customTitle;
    }

    // Apply custom subtitle if configured
    const customSubtitle = getNestedString(childConfig, 'subtitle', '');

    if (customSubtitle) {
        customizations.subtitle = customSubtitle;
    }

    // Apply custom icon if configured
    const customIcon = getNestedString(childConfig, 'icon', '');

    if (customIcon) {
        customizations.icon = customIcon;
    }

    // Apply custom order if configured
    if (typeof childConfig.order === 'number') {
        customizations.order = childConfig.order;
    }

    // Apply custom URL if configured
    const customUrl = getNestedString(childConfig, 'url', '');

    if (customUrl) {
        customizations.url = customUrl;
    }

    // Return merged child with customizations
    return {
        ...child,
        ...customizations
    };
};

/**
 * Recursively apply customizations, permission filtering, and disabled/hidden
 * filtering to a list of nav items and their descendants. Config nesting mirrors
 * the nav tree via a "children" key at each level, so this supports any depth
 * (e.g. transfers.children.transfer-dashboard).
 * @param children - The child navigation items to process
 * @param configNode - Config object keyed by item id at this level
 * @param userRoles - Array of user's roles for permission checking
 * @returns Filtered, customized, and order-sorted children
 */
const applyConfigToChildren = (
    children: FuseNavItemType[],
    configNode: ConfigValues | undefined,
    userRoles?: Role[] | null
): FuseNavItemType[] => {
    if (!configNode || typeof configNode !== 'object') {
        return children;
    }

    const processedChildren = children
        .map(child => {
            const childConfig = configNode[child.id];
            let updatedChild = childConfig && typeof childConfig === 'object'
                ? applyConfigToChild(child, childConfig as ConfigValues)
                : child;

            if (updatedChild.children && updatedChild.children.length > 0) {
                const nestedConfig = childConfig && typeof childConfig === 'object'
                    ? ((childConfig as ConfigValues).children as ConfigValues | undefined)
                    : undefined;

                updatedChild = {
                    ...updatedChild,
                    children: applyConfigToChildren(updatedChild.children, nestedConfig, userRoles)
                };
            }

            return updatedChild;
        })
        .filter(child => {
            const childConfig = configNode[child.id];

            if (childConfig && typeof childConfig === 'object') {
                const childConfigValues = childConfig as ConfigValues;

                // Check if hidden or disabled
                const isHidden = getNestedBoolean(childConfigValues, 'hidden', false);
                const isDisabled = getNestedBoolean(childConfigValues, 'disabled', false);

                if (isHidden || isDisabled) {
                    return false;
                }

                // Check permissions for child items
                const permissions = childConfigValues.permissions;

                if (!hasPermission(permissions as ConfigValues, userRoles)) {
                    return false;
                }
            }

            // If no permissions specified, include the child
            return true;
        });

    // Sort children by order if specified
    return processedChildren.sort((a, b) => {
        const orderA = typeof a.order === 'number' ? a.order : Infinity;
        const orderB = typeof b.order === 'number' ? b.order : Infinity;
        return orderA - orderB;
    });
};

/**
 * Apply configuration overrides to a navigation item and its children
 * This function merges custom properties from config into the navigation item recursively
 *
 * @param item - The navigation item to customize
 * @param config - The configuration values
 * @param configMapping - The configuration mapping for this item
 * @param userRoles - Array of user's roles for permission checking
 * @returns Navigation item with applied customizations
 */
const applyConfigToNavItem = (
    item: FuseNavItemType,
    config: ConfigValues,
    configMapping: NavigationConfigMapping,
    userRoles?: Role[] | null
): FuseNavItemType => {
    const customizations: Partial<FuseNavItemType> = {};

    // Apply custom title if configured
    if (configMapping.titlePath) {
        const customTitle = getNestedString(config, configMapping.titlePath, '');

        if (customTitle) {
            customizations.title = customTitle;
        }
    }

    // Apply custom subtitle if configured
    if (configMapping.subtitlePath) {
        const customSubtitle = getNestedString(config, configMapping.subtitlePath, '');

        if (customSubtitle) {
            customizations.subtitle = customSubtitle;
        }
    }

    // Apply custom icon if configured
    const customIcon = getNestedString(config, 'navigation.icon', '');

    if (customIcon) {
        customizations.icon = customIcon;
    }

    // Apply custom order if configured
    if (config.navigation && typeof config.navigation === 'object' && 'order' in config.navigation) {
        const navConfig = config.navigation as { order?: number; };

        if (typeof navConfig.order === 'number') {
            customizations.order = navConfig.order;
        }
    }

    // Apply custom URL if configured
    const customUrl = getNestedString(config, 'navigation.url', '') || getNestedString(config, 'url', '');

    if (customUrl) {
        customizations.url = customUrl;
    }

    // Apply customizations to children if they exist
    if (item.children && item.children.length > 0 && configMapping.childrenConfigPath) {
        try {
            let currentLevel: ConfigValues | undefined = config;

            // Navigate to the children config path
            const pathParts = configMapping.childrenConfigPath.split('.');

            for (const part of pathParts) {
                if (currentLevel && typeof currentLevel === 'object' && part in currentLevel) {
                    currentLevel = currentLevel[part] as ConfigValues;
                } else {
                    currentLevel = undefined;
                    break;
                }
            }

            if (currentLevel && typeof currentLevel === 'object') {
                // Recursively apply customizations/filtering to children and their descendants
                customizations.children = applyConfigToChildren(item.children, currentLevel, userRoles);
            }
        } catch (error) {
            console.warn(`Failed to apply children customizations for ${item.id}:`, error);
        }
    }

    // Return merged item with customizations
    return {
        ...item,
        ...customizations
    };
};

/**
 * Get filtered navigation based on configuration settings and user permissions
 * This function:
 * - Removes navigation items that are disabled via configuration
 * - Filters navigation items based on user roles/permissions
 * - Applies custom titles, subtitles, and other properties from config
 *
 * @param configs - Object containing all loaded configurations
 * @param navigation - Optional navigation array to filter (defaults to defaultNavigationConfig)
 * @param userRoles - Optional array of user roles for permission checking
 * @returns Filtered and customized navigation array
 */
export const getFilteredNavigation = (
    configs: Record<string, ConfigValues> = {},
    navigation: FuseNavItemType[] = defaultNavigationConfig,
    userRoles?: Role[] | null
): FuseNavItemType[] => {
    try {
        const processedNavigation = navigation
            .filter(item => {
                // Get the configuration mapping for this navigation item
                const configMapping = navigationConfigMap[item.id];

                if (!configMapping) {
                    // If no configuration mapping exists, include the item by default
                    return true;
                }

                try {
                    // Get the specific configuration for this navigation item
                    const config = configs[configMapping.configName];

                    if (!config) {
                        // If config not loaded yet, include the item by default
                        return true;
                    }

                    const isDisabled = getNestedBoolean(config, configMapping.disablePath, false);

                    // Check if item is disabled
                    if (isDisabled) {
                        return false;
                    }

                    // Check permissions if configured
                    if (configMapping.permissionsPath) {
                        const permissions = config[configMapping.permissionsPath];

                        if (!hasPermission(permissions as ConfigValues, userRoles)) {
                            return false;
                        }
                    }

                    return true;
                } catch (error) {
                    // If checking this specific config fails, include the item by default to avoid breaking the app
                    console.warn(`Failed to check ${configMapping.configName} for navigation filtering:`, error);
                    return true;
                }
            })
            .map(item => {
                // Apply configuration customizations to each item
                const configMapping = navigationConfigMap[item.id];

                if (!configMapping) {
                    // No customizations for this item
                    return item;
                }

                try {
                    const config = configs[configMapping.configName];

                    if (!config) {
                        // Config not loaded, return original item
                        return item;
                    }

                    return applyConfigToNavItem(item, config, configMapping, userRoles);
                } catch (error) {
                    console.warn(`Failed to apply config customizations to ${item.id}:`, error);
                    return item;
                }
            });

        // Sort navigation items by order if specified
        const sortedNavigation = processedNavigation.sort((a, b) => {
            const orderA = typeof a.order === 'number' ? a.order : Infinity;
            const orderB = typeof b.order === 'number' ? b.order : Infinity;
            return orderA - orderB;
        });

        return sortedNavigation;
    } catch (error) {
        // If the entire filtering process fails, return full navigation to avoid breaking the app
        console.warn('Failed to filter navigation based on configurations:', error);
        return navigation;
    }
};// Keep backward compatibility

export default {
    getFilteredNavigation
};
